import UserEventLog from "./user-event-log";
import {setMessage, setNotifications, upsertNotification} from "../../system/app-slice";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    setWorkspaceGraphInboxItems,
    upsertWorkspaceInboxItem,
} from "../../system/workspace-graph-slice";
import {normalizeMembershipEntity} from "../workspace-membership/workspace-membership-identity";
import {buildUnreadUserEventLogQuery} from "./user-event-log-queries";

const inboxPopulate = {
    actor: {populate: {member: {populate: {profileImage: true}}}},
    workspace: {fields: ["name", "documentId"]},
    audit_log: {fields: ["documentId"]},
};

const workspaceKey = (workspace) => workspace?.documentId ?? workspace?.id ?? null;

// personal notifications (security alerts, scheduled reports) have no workspace and appear in every workspace
const belongsToWorkspace = (item, workspace) => {
    const itemWorkspaceKey = workspaceKey(item?.workspace);
    const activeWorkspaceKey = workspaceKey(workspace);
    if (itemWorkspaceKey === null && activeWorkspaceKey !== null) return true;
    return itemWorkspaceKey !== null
        && activeWorkspaceKey !== null
        && String(itemWorkspaceKey) === String(activeWorkspaceKey);
};

export default function useUserEventLog() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const {user} = useSelector(state => state.auth);
    const {workspaceMembershipData} = useSelector(state => state.app);
    const memberships = workspaceMembershipData?.workspace?.workspace_memberships || [];
    const dispatch = useDispatch();

    const run = async (action) => {
        setLoading(true);
        setError(null);
        try {
            return await action();
        } catch (requestError) {
            setError(requestError);
            throw requestError;
        } finally {
            setLoading(false);
        }
    };

    const getUserEventLogs = async () => {
        return run(async () => {
            const userEventLog = new UserEventLog();
            const response = await userEventLog.getAll(buildUnreadUserEventLogQuery(user?.id));
            const items = (response?.data || []).map((item) =>
                normalizeMembershipEntity(item, "userEventLog", memberships));

            items.filter((item) => !item.isRead && item.displayType === "toast")
                .forEach((item) => dispatch(setMessage({severity: "info", content: item.message})));
            dispatch(setNotifications(items));
            return items;
        }).catch(() => []);
    };

    const getInboxItems = async () => {
        const workspace = workspaceMembershipData?.workspace;
        const activeWorkspaceKey = workspaceKey(workspace);
        if (activeWorkspaceKey === null) {
            dispatch(setWorkspaceGraphInboxItems([]));
            return [];
        }

        return run(async () => {
            const response = await new UserEventLog().getAll({
                filters: {
                    $or: [
                        {
                            workspace: workspace?.documentId
                                ? {documentId: {$eq: workspace.documentId}}
                                : {id: {$eq: workspace.id}},
                        },
                        {workspace: {id: {$null: true}}},
                    ],
                },
                populate: inboxPopulate,
                sort: ["createdAt:desc"],
                pagination: {pageSize: 300},
            });
            const items = (response?.data || [])
                .map((item) => normalizeMembershipEntity(item, "userEventLog", memberships))
                .filter((item) => item.displayType !== "invisible")
                .filter((item) => belongsToWorkspace(item, workspace));
            dispatch(setWorkspaceGraphInboxItems(items));
            return items;
        });
    };

    const getActionRequiredItems = async () => {
        const workspace = workspaceMembershipData?.workspace;
        const activeWorkspaceKey = workspaceKey(workspace);
        if (activeWorkspaceKey === null || !user?.id) return [];

        return run(async () => {
            const response = await new UserEventLog().getAll({
                filters: {
                    $and: [
                        {
                            workspace: workspace?.documentId
                                ? {documentId: {$eq: workspace.documentId}}
                                : {id: {$eq: workspace.id}},
                        },
                        {owner: {id: {$eq: user.id}}},
                        {eventCategory: {$eq: "mention"}},
                        {requiresResponse: {$eq: true}},
                    ],
                },
                populate: inboxPopulate,
                sort: ["createdAt:asc"],
                pagination: {pageSize: 100},
            });
            return (response?.data || [])
                .map((item) => normalizeMembershipEntity(item, "userEventLog", memberships))
                .filter((item) => belongsToWorkspace(item, workspace));
        });
    };

    const getUserEventLog = async (documentId) => run(async () => {
        try {
            const response = await new UserEventLog().getById(documentId, {populate: inboxPopulate});
            return normalizeMembershipEntity(response?.data, "userEventLog", memberships) || null;
        } catch (requestError) {
            if ([403, 404].includes(requestError?.status)) return null;
            throw requestError;
        }
    });

    const updateUserEventLog = async (documentId, data) => run(async () => {
        const response = await new UserEventLog().updateItem(documentId, data, {populate: inboxPopulate});
        const item = normalizeMembershipEntity(response?.data, "userEventLog", memberships);
        if (item) {
            if (belongsToWorkspace(item, workspaceMembershipData?.workspace)) {
                dispatch(upsertWorkspaceInboxItem(item));
            }
            dispatch(upsertNotification(item));
        }
        return item;
    });

    // the bell marks everything (all workspaces); the Inbox tab only the active workspace
    const markAllRead = async ({allWorkspaces = false} = {}) => {
        const workspaceDocumentId = workspaceMembershipData?.workspace?.documentId;
        if (!workspaceDocumentId && !allWorkspaces) return [];

        return run(async () => {
            const response = await new UserEventLog().putAction(
                "/user-event-logs/read-all",
                {},
                allWorkspaces ? {} : {workspaceDocumentId},
            );
            const items = response?.data || [];
            items.forEach((item) => {
                dispatch(upsertWorkspaceInboxItem(item));
                dispatch(upsertNotification(item));
            });
            return items;
        });
    };

    return {
        getUserEventLogs,
        getInboxItems,
        getActionRequiredItems,
        getUserEventLog,
        updateUserEventLog,
        markAllRead,
        loading,
        error,
    };
}
